/**
 * Assignment Service
 * Handles all assignment operations with Firebase
 */

import { 
  collection, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs, 
  getDoc,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  Timestamp,
  increment
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../../services/firebase/firebase';
import { getAuth } from 'firebase/auth';
import notificationService from '../../../services/firebase/notificationService';

/**
 * Upload assignment file to Firebase Storage
 * 
 * @param {File} file - The file to upload
 * @param {string} userId - The user ID
 * @returns {Promise<string>} Download URL of the uploaded file
 */
const uploadAssignmentFile = async (file, userId) => {
  try {
    const fileExtension = file.name.split('.').pop();
    const fileName = `${userId}_${Date.now()}.${fileExtension}`;
    
    // Update to use the simpler path that matches the storage rules
    const storageRef = ref(storage, `assignments/${fileName}`);
    
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  } catch (error) {
    console.error('Error uploading assignment file:', error);
    throw error;
  }
};

/**
 * Create a new assignment request
 * 
 * @param {Object} assignmentData - Assignment data
 * @param {File} file - Assignment file
 * @returns {Promise<string>} Assignment ID
 */
const createAssignment = async (assignmentData, file) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to create an assignment');
    }
    
    // Upload file first
    const fileUrl = await uploadAssignmentFile(file, currentUser.uid);
    
    // Create assignment document
    const assignmentRef = await addDoc(collection(db, 'assignments'), {
      title: assignmentData.title,
      description: assignmentData.description,
      subject: assignmentData.subject,
      course: assignmentData.course,
      dueDate: assignmentData.dueDate instanceof Date ? Timestamp.fromDate(assignmentData.dueDate) : assignmentData.dueDate,
      budget: Number(assignmentData.budget),
      timeEstimate: assignmentData.timeEstimate,
      fileUrl,
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
      userId: currentUser.uid,
      status: 'open', // 'open', 'assigned', 'completed', 'cancelled'
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      viewCount: 0,
      offerCount: 0,
      university: assignmentData.university,
      yearLevel: assignmentData.yearLevel,
      academicIntegrityAgreed: true,
      tutorId: null,
      completedAt: null
    });
    
    return assignmentRef.id;
  } catch (error) {
    console.error('Error creating assignment:', error);
    throw error;
  }
};

/**
 * Get user assignments
 * 
 * @param {string} userId - The user ID
 * @param {number} limitCount - Number of assignments to retrieve
 * @returns {Promise<Array>} Array of assignment objects
 */
const getUserAssignments = async (userId, limitCount = 10) => {
  try {
    const assignmentsQuery = query(
      collection(db, 'assignments'),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );
    
    const snapshot = await getDocs(assignmentsQuery);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt instanceof Timestamp ? 
        doc.data().createdAt.toDate() : 
        new Date(),
      updatedAt: doc.data().updatedAt instanceof Timestamp ? 
        doc.data().updatedAt.toDate() : 
        new Date(),
      dueDate: doc.data().dueDate instanceof Timestamp ? 
        doc.data().dueDate.toDate() : 
        doc.data().dueDate
    }));
  } catch (error) {
    console.error('Error getting user assignments:', error);
    throw error;
  }
};

/**
 * Delete an assignment
 * 
 * @param {string} assignmentId - The assignment ID
 * @returns {Promise<void>}
 */
const deleteAssignment = async (assignmentId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to delete an assignment');
    }
    
    // Get assignment data
    const assignmentRef = doc(db, 'assignments', assignmentId);
    const assignmentSnap = await getDoc(assignmentRef);
    
    if (!assignmentSnap.exists()) {
      throw new Error('Assignment not found');
    }
    
    const assignmentData = assignmentSnap.data();
    
    // Check if user owns this assignment
    if (assignmentData.userId !== currentUser.uid) {
      throw new Error('You can only delete your own assignments');
    }
    
    // Delete file from storage if exists
    if (assignmentData.fileUrl) {
      try {
        const fileRef = ref(storage, assignmentData.fileUrl);
        await deleteObject(fileRef);
      } catch (error) {
        console.error('Error deleting assignment file:', error);
        // Continue with assignment deletion even if file deletion fails
      }
    }
    
    // Delete assignment document
    await deleteDoc(assignmentRef);
  } catch (error) {
    console.error('Error deleting assignment:', error);
    throw error;
  }
};

/**
 * Get available assignments (open and not created by the current user)
 * 
 * @param {Object} filters - Optional filters for assignments
 * @param {number} limitCount - Number of assignments to retrieve
 * @returns {Promise<Array>} Array of assignment objects
 */
const getAvailableAssignments = async (filters = {}, limitCount = 10) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to view available assignments');
    }
    
    // Start with basic query
    let assignmentsQuery = query(
      collection(db, 'assignments'),
      where('status', '==', 'open'),
      where('userId', '!=', currentUser.uid)
    );
    
    // Add filters if provided
    if (filters.university) {
      assignmentsQuery = query(
        collection(db, 'assignments'),
        where('status', '==', 'open'),
        where('userId', '!=', currentUser.uid),
        where('university', '==', filters.university)
      );
    }
    
    if (filters.subject) {
      assignmentsQuery = query(
        collection(db, 'assignments'),
        where('status', '==', 'open'),
        where('userId', '!=', currentUser.uid),
        where('subject', '==', filters.subject)
      );
    }
    
    // Get the documents with the final query
    const snapshot = await getDocs(assignmentsQuery);
    
    // Sort results by createdAt and limit them manually
    // since we can't combine multiple where clauses with orderBy in Firestore
    const sorted = snapshot.docs
      .map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt instanceof Timestamp ? 
          doc.data().createdAt.toDate() : 
          new Date(),
        updatedAt: doc.data().updatedAt instanceof Timestamp ? 
          doc.data().updatedAt.toDate() : 
          new Date(),
        dueDate: doc.data().dueDate instanceof Timestamp ? 
          doc.data().dueDate.toDate() : 
          doc.data().dueDate
      }))
      .sort((a, b) => b.createdAt - a.createdAt)
      .slice(0, limitCount);
    
    return sorted;
  } catch (error) {
    console.error('Error getting available assignments:', error);
    throw error;
  }
};

/**
 * Create an offer for an assignment
 * 
 * @param {string} assignmentId - The assignment ID
 * @param {Object} offerData - Offer data
 * @returns {Promise<string>} Offer ID
 */
const createOffer = async (assignmentId, offerData) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to make an offer');
    }
    
    // Get assignment to check status and get owner
    const assignmentRef = doc(db, 'assignments', assignmentId);
    const assignmentSnap = await getDoc(assignmentRef);
    
    if (!assignmentSnap.exists()) {
      throw new Error('Assignment not found');
    }
    
    const assignmentData = assignmentSnap.data();
    
    // Check if assignment is still open
    if (assignmentData.status !== 'open') {
      throw new Error('This assignment is no longer accepting offers');
    }
    
    // Check that user isn't offering on their own assignment
    if (assignmentData.userId === currentUser.uid) {
      throw new Error('You cannot make an offer on your own assignment');
    }
    
    // Create the offer
    const offerRef = await addDoc(collection(db, `assignments/${assignmentId}/offers`), {
      tutorId: currentUser.uid,
      price: Number(offerData.price),
      message: offerData.message,
      timeEstimate: offerData.timeEstimate,
      status: 'pending', // 'pending', 'accepted', 'rejected'
      createdAt: serverTimestamp()
    });
    
    // Update assignment offer count
    await updateDoc(assignmentRef, {
      offerCount: increment(1),
      updatedAt: serverTimestamp()
    });
    
    // Get tutor profile info for notification
    const tutorProfileRef = doc(db, 'profiles', currentUser.uid);
    const tutorProfileSnap = await getDoc(tutorProfileRef);
    const tutorProfile = tutorProfileSnap.exists() ? tutorProfileSnap.data() : {};
    
    // Create notification for assignment owner
    await notificationService.createNotification({
      userId: assignmentData.userId,
      type: 'assignment_offer',
      content: {
        assignmentId,
        assignmentTitle: assignmentData.title,
        tutorId: currentUser.uid,
        tutorName: tutorProfile.basicInfo?.name || 'Someone',
        tutorPhoto: tutorProfile.photos && tutorProfile.photos.length > 0 ? tutorProfile.photos[0] : null,
        offerPrice: offerData.price
      }
    });
    
    return offerRef.id;
  } catch (error) {
    console.error('Error creating offer:', error);
    throw error;
  }
};

/**
 * Get offers for an assignment
 * 
 * @param {string} assignmentId - The assignment ID
 * @returns {Promise<Array>} Array of offer objects
 */
const getAssignmentOffers = async (assignmentId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to view offers');
    }
    
    // Get assignment to check if user owns it
    const assignmentRef = doc(db, 'assignments', assignmentId);
    const assignmentSnap = await getDoc(assignmentRef);
    
    if (!assignmentSnap.exists()) {
      throw new Error('Assignment not found');
    }
    
    const assignmentData = assignmentSnap.data();
    
    // Only the assignment owner can see all offers
    if (assignmentData.userId !== currentUser.uid) {
      // For non-owners, return only their own offers
      const offersQuery = query(
        collection(db, `assignments/${assignmentId}/offers`),
        where('tutorId', '==', currentUser.uid)
      );
      
      const snapshot = await getDocs(offersQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt instanceof Timestamp ? 
          doc.data().createdAt.toDate() : 
          new Date()
      }));
    }
    
    // For the owner, get all offers with tutor information
    const offersQuery = query(
      collection(db, `assignments/${assignmentId}/offers`),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(offersQuery);
    const offers = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt instanceof Timestamp ? 
        doc.data().createdAt.toDate() : 
        new Date()
    }));
    
    // Get tutor profiles in a batch
    const tutorIds = offers.map(offer => offer.tutorId);
    const uniqueTutorIds = [...new Set(tutorIds)];
    
    const tutorProfiles = {};
    await Promise.all(uniqueTutorIds.map(async (tutorId) => {
      const tutorProfileRef = doc(db, 'profiles', tutorId);
      const tutorProfileSnap = await getDoc(tutorProfileRef);
      
      if (tutorProfileSnap.exists()) {
        tutorProfiles[tutorId] = tutorProfileSnap.data();
      }
    }));
    
    // Add tutor info to offers
    return offers.map(offer => ({
      ...offer,
      tutorProfile: tutorProfiles[offer.tutorId] || null
    }));
  } catch (error) {
    console.error('Error getting assignment offers:', error);
    throw error;
  }
};

/**
 * Accept an offer for an assignment
 * 
 * @param {string} assignmentId - The assignment ID
 * @param {string} offerId - The offer ID
 * @returns {Promise<void>}
 */
const acceptOffer = async (assignmentId, offerId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to accept an offer');
    }
    
    // Get assignment to check if user owns it
    const assignmentRef = doc(db, 'assignments', assignmentId);
    const assignmentSnap = await getDoc(assignmentRef);
    
    if (!assignmentSnap.exists()) {
      throw new Error('Assignment not found');
    }
    
    const assignmentData = assignmentSnap.data();
    
    // Check if user owns this assignment
    if (assignmentData.userId !== currentUser.uid) {
      throw new Error('You can only accept offers on your own assignments');
    }
    
    // Check if assignment is still open
    if (assignmentData.status !== 'open') {
      throw new Error('This assignment is no longer accepting offers');
    }
    
    // Get the specific offer
    const offerRef = doc(db, `assignments/${assignmentId}/offers`, offerId);
    const offerSnap = await getDoc(offerRef);
    
    if (!offerSnap.exists()) {
      throw new Error('Offer not found');
    }
    
    const offerData = offerSnap.data();
    
    // Update the offer status
    await updateDoc(offerRef, {
      status: 'accepted'
    });
    
    // Update the assignment status and tutorId
    await updateDoc(assignmentRef, {
      status: 'assigned',
      tutorId: offerData.tutorId,
      updatedAt: serverTimestamp()
    });
    
    // Mark other offers as rejected
    const otherOffersQuery = query(
      collection(db, `assignments/${assignmentId}/offers`),
      where('status', '==', 'pending')
    );
    
    const otherOffersSnapshot = await getDocs(otherOffersQuery);
    
    await Promise.all(otherOffersSnapshot.docs.map(doc => {
      if (doc.id !== offerId) {
        return updateDoc(doc.ref, { status: 'rejected' });
      }
      return Promise.resolve();
    }));
    
    // Create notification for the tutor
    await notificationService.createNotification({
      userId: offerData.tutorId,
      type: 'offer_accepted',
      content: {
        assignmentId,
        assignmentTitle: assignmentData.title,
        ownerId: currentUser.uid,
        offerPrice: offerData.price
      }
    });
  } catch (error) {
    console.error('Error accepting offer:', error);
    throw error;
  }
};

/**
 * Mark an assignment as completed
 * 
 * @param {string} assignmentId - The assignment ID
 * @returns {Promise<void>}
 */
const completeAssignment = async (assignmentId) => {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      throw new Error('You must be logged in to complete an assignment');
    }
    
    // Get assignment to check ownership
    const assignmentRef = doc(db, 'assignments', assignmentId);
    const assignmentSnap = await getDoc(assignmentRef);
    
    if (!assignmentSnap.exists()) {
      throw new Error('Assignment not found');
    }
    
    const assignmentData = assignmentSnap.data();
    
    // Check if user is the assignment owner
    if (assignmentData.userId !== currentUser.uid) {
      throw new Error('Only the assignment owner can mark it as completed');
    }
    
    // Check if assignment is in the assigned state
    if (assignmentData.status !== 'assigned') {
      throw new Error('Only assigned assignments can be marked as completed');
    }
    
    // Update assignment status
    await updateDoc(assignmentRef, {
      status: 'completed',
      completedAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });
    
    // Create notification for the tutor
    if (assignmentData.tutorId) {
      await notificationService.createNotification({
        userId: assignmentData.tutorId,
        type: 'assignment_completed',
        content: {
          assignmentId,
          assignmentTitle: assignmentData.title,
          ownerId: currentUser.uid
        }
      });
    }
  } catch (error) {
    console.error('Error completing assignment:', error);
    throw error;
  }
};

/**
 * Get a single assignment by ID
 * 
 * @param {string} assignmentId - The assignment ID
 * @returns {Promise<Object|null>} Assignment object or null if not found
 */
const getAssignmentById = async (assignmentId) => {
  try {
    const assignmentRef = doc(db, 'assignments', assignmentId);
    const assignmentSnap = await getDoc(assignmentRef);
    
    if (!assignmentSnap.exists()) {
      return null;
    }
    
    const assignmentData = assignmentSnap.data();
    
    // Increment view count
    await updateDoc(assignmentRef, {
      viewCount: increment(1)
    });
    
    return {
      id: assignmentSnap.id,
      ...assignmentData,
      createdAt: assignmentData.createdAt instanceof Timestamp ? 
        assignmentData.createdAt.toDate() : 
        new Date(),
      updatedAt: assignmentData.updatedAt instanceof Timestamp ? 
        assignmentData.updatedAt.toDate() : 
        new Date(),
      dueDate: assignmentData.dueDate instanceof Timestamp ? 
        assignmentData.dueDate.toDate() : 
        assignmentData.dueDate,
      completedAt: assignmentData.completedAt instanceof Timestamp ? 
        assignmentData.completedAt.toDate() : 
        assignmentData.completedAt
    };
  } catch (error) {
    console.error('Error getting assignment by ID:', error);
    throw error;
  }
};

export default {
  createAssignment,
  uploadAssignmentFile,
  getUserAssignments,
  deleteAssignment,
  getAvailableAssignments,
  createOffer,
  getAssignmentOffers,
  acceptOffer,
  completeAssignment,
  getAssignmentById
};